// Centralised access to Vite-injected env. Keeps `import.meta.env`
// reads in one place so flow rewrites (Phase 4 → 6) touch a single file.
//
// Phase 4 Task 5b 以降:
// - WS URL は **dispatcher 由来** (`POST /rooms` / `GET /rooms/:id/server`
//   のレスポンスに含まれる `wsUrl`) のみを使用する.
// - ハードコードや `VITE_WS_URL` 経由で wsUrl を持つ実装は削除済み.
// - dispatcher の base URL のみが env で渡される.
//
// 2026-05-21: dev 時の LAN アクセス (= スマホ / 液タブ機から `http://192.168.x.x:5173`)
// を localhost 解決失敗で壊さないため、`VITE_DISPATCHER_URL` 未指定時は
// `window.location.hostname` ベースで動的に組み立てる. prod は env 固定が
// 前提のため動的経路は走らない.

const FALLBACK_DISPATCHER_URL = 'http://localhost:3000';
const DISPATCHER_PORT = 3000;

/**
 * `VITE_DISPATCHER_URL` 未指定時の動的 fallback. ブラウザ環境では
 * `window.location.hostname` を流用して dispatcher URL を組み立てる.
 *
 * - SSR / Node (= test) 環境: `'http://localhost:3000'`
 * - `file:` / `chrome-extension:` 等 http(s) 以外: `'http://localhost:3000'`
 * - 通常ブラウザ: `${protocol}//${hostname}:3000`
 */
export const detectDispatcherUrl = (): string => {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return FALLBACK_DISPATCHER_URL;
  }
  const { protocol, hostname } = window.location;
  if (protocol !== 'http:' && protocol !== 'https:') {
    return FALLBACK_DISPATCHER_URL;
  }
  if (!hostname) {
    return FALLBACK_DISPATCHER_URL;
  }
  return `${protocol}//${hostname}:${DISPATCHER_PORT}`;
};

export interface WebEnv {
  /** dispatcher HTTP base URL. e.g. `http://localhost:3000`. */
  readonly dispatcherUrl: string;
}

export const readEnv = (): WebEnv => {
  // `import.meta.env.VITE_DISPATCHER_URL` is replaced at build time by Vite.
  // 明示指定があれば最優先 (= prod / 明示 dev). 未指定なら動的検出にフォール
  // バック (= LAN アクセス自動追従).
  const raw = import.meta.env.VITE_DISPATCHER_URL;
  return {
    dispatcherUrl: typeof raw === 'string' && raw.length > 0 ? raw : detectDispatcherUrl(),
  };
};
